import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { DriveImportResult, DriveListResult, DriveRemoteItem, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../../ws/WsProvider.js';
import { DRIVE_STATUS_QUERY_KEY } from './useDriveConnection.js';

export interface DriveBreadcrumb {
  id: string | null;
  name: string;
}

export interface UseDriveBrowserResult {
  items: readonly DriveRemoteItem[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  folderId: string | null;
  breadcrumbs: readonly DriveBreadcrumb[];
  navigateInto: (item: DriveRemoteItem) => void;
  navigateToBreadcrumb: (index: number) => void;
  q: string;
  setQ: (value: string) => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  selectedIds: ReadonlySet<string>;
  toggleSelect: (item: DriveRemoteItem) => void;
  clearSelection: () => void;
  importSelected: (targetFolderId: string | null) => Promise<DriveImportResult>;
  isImporting: boolean;
  importError: string | null;
}

/**
 * F6 Drive picker browse state — cursor-only (`driveListQuery`'s own doc
 * comment: no local total, an opaque `pageToken`, deliberately NOT the
 * `listQueryInput`/`paginated` contract), so this is a "Load more" append
 * list, never numbered pages, with a CLIENT-tracked breadcrumb trail (the
 * live Drive API response carries no breadcrumb chain of its own, unlike
 * `file.list`'s local `breadcrumbs`).
 */
export function useDriveBrowser(enabled: boolean): UseDriveBrowserResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const [folderId, setFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<DriveBreadcrumb[]>([]);
  const [q, setQ] = useState('');
  const [items, setItems] = useState<DriveRemoteItem[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());

  const isReady = enabled && connectionState === 'connected';

  const query = useQuery<DriveListResult, ErrorAlert>({
    queryKey: ['drive-list', folderId, q],
    queryFn: () => requestTyped(wsClient, 'drive.list', { folderId, pageToken: null, q: q || undefined }),
    enabled: isReady,
  });

  useEffect(() => {
    if (!query.data) return;
    setItems(query.data.items);
    setNextPageToken(query.data.nextPageToken);
  }, [query.data]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [folderId]);

  const loadMoreMutation = useMutation<DriveListResult, ErrorAlert>({
    mutationFn: () => requestTyped(wsClient, 'drive.list', { folderId, pageToken: nextPageToken, q: q || undefined }),
    onSuccess: (data) => {
      setItems((prev) => [...prev, ...data.items]);
      setNextPageToken(data.nextPageToken);
    },
  });

  const importMutation = useMutation<DriveImportResult, ErrorAlert, string | null>({
    mutationFn: (targetFolderId) =>
      requestTyped(wsClient, 'drive.import', { fileIds: Array.from(selectedIds), folderId: targetFolderId }),
    onSuccess: () => {
      setSelectedIds(new Set());
      void queryClient.invalidateQueries({ queryKey: DRIVE_STATUS_QUERY_KEY });
    },
  });

  const navigateInto = useCallback((item: DriveRemoteItem) => {
    if (!item.isFolder) return;
    setBreadcrumbs((prev) => [...prev, { id: item.id, name: item.name }]);
    setFolderId(item.id);
  }, []);

  const navigateToBreadcrumb = useCallback((index: number) => {
    if (index < 0) {
      setBreadcrumbs([]);
      setFolderId(null);
      return;
    }
    setBreadcrumbs((prev) => {
      const next = prev.slice(0, index + 1);
      setFolderId(next[next.length - 1]?.id ?? null);
      return next;
    });
  }, []);

  const toggleSelect = useCallback((item: DriveRemoteItem) => {
    if (item.isFolder) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  return {
    items,
    isLoading: query.isLoading,
    isError: query.isError,
    onRetry: () => void query.refetch(),
    folderId,
    breadcrumbs,
    navigateInto,
    navigateToBreadcrumb,
    q,
    setQ,
    hasMore: nextPageToken !== null,
    isLoadingMore: loadMoreMutation.isPending,
    loadMore: () => void loadMoreMutation.mutate(),
    selectedIds,
    toggleSelect,
    clearSelection,
    importSelected: (targetFolderId: string | null) => importMutation.mutateAsync(targetFolderId),
    isImporting: importMutation.isPending,
    importError: importMutation.isError ? importMutation.error.message : null,
  };
}
